const STORAGE_KEYS = {
	zoom: 'lecardo-vein-a11y-zoom',
	grayscale: 'lecardo-vein-a11y-grayscale',
};

const ZOOM_CLASSES = [
	'a11y-zoom-1',
	'a11y-zoom-2',
];

/**
 * Read a local accessibility preference.
 *
 * @param {string} key Storage key.
 * @return {string|null}
 */
function getPreference(key) {
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
}

/**
 * Persist a local accessibility preference.
 *
 * @param {string} key Storage key.
 * @param {string} value Storage value.
 * @return {void}
 */
function setPreference(key, value) {
	try {
		window.localStorage.setItem(key, value);
	} catch {
		// The controls still work when storage is unavailable.
	}
}

/**
 * Initialize text zoom and grayscale accessibility controls.
 *
 * @return {void}
 */
export function initAccessibilityControls() {
	const controls = Array.from(document.querySelectorAll('[data-a11y-controls]'));

	if (0 === controls.length) {
		return;
	}

	const storedZoom = Number.parseInt(getPreference(STORAGE_KEYS.zoom) || '0', 10);
	let zoomLevel = Number.isInteger(storedZoom)
		? Math.min(Math.max(storedZoom, 0), ZOOM_CLASSES.length)
		: 0;
	let grayscale = 'true' === getPreference(STORAGE_KEYS.grayscale);

	const applyPreferences = () => {
		document.body.classList.remove(...ZOOM_CLASSES);

		if (0 < zoomLevel) {
			document.body.classList.add(ZOOM_CLASSES[zoomLevel - 1]);
		}

		document.documentElement.classList.toggle('a11y-grayscale', grayscale);
		setPreference(STORAGE_KEYS.zoom, String(zoomLevel));
		setPreference(STORAGE_KEYS.grayscale, String(grayscale));

		controls.forEach((control) => {
			const decrease = control.querySelector('[data-a11y-decrease]');
			const increase = control.querySelector('[data-a11y-increase]');
			const contrast = control.querySelector('[data-a11y-contrast]');

			if (decrease) {
				decrease.disabled = 0 === zoomLevel;
			}

			if (increase) {
				increase.disabled = ZOOM_CLASSES.length === zoomLevel;
			}

			contrast?.setAttribute('aria-pressed', String(grayscale));
		});
	};

	controls.forEach((control) => {
		const toggle = control.querySelector('[data-a11y-toggle]');
		const panel = control.querySelector('[data-a11y-panel]');
		const decrease = control.querySelector('[data-a11y-decrease]');
		const increase = control.querySelector('[data-a11y-increase]');
		const contrast = control.querySelector('[data-a11y-contrast]');

		if (!toggle || !panel || !decrease || !increase || !contrast) {
			return;
		}

		const setOpen = (isOpen, returnFocus = false) => {
			control.classList.toggle('is-open', isOpen);
			toggle.setAttribute('aria-expanded', String(isOpen));
			panel.setAttribute('aria-hidden', String(!isOpen));

			if (returnFocus) {
				toggle.focus();
			}
		};

		toggle.addEventListener('click', () => {
			setOpen(!control.classList.contains('is-open'));
		});

		decrease.addEventListener('click', () => {
			zoomLevel = Math.max(0, zoomLevel - 1);
			applyPreferences();
		});

		increase.addEventListener('click', () => {
			zoomLevel = Math.min(ZOOM_CLASSES.length, zoomLevel + 1);
			applyPreferences();
		});

		contrast.addEventListener('click', () => {
			grayscale = !grayscale;
			applyPreferences();
		});

		document.addEventListener('click', (event) => {
			if (!control.contains(event.target)) {
				setOpen(false);
			}
		});

		control.addEventListener('keydown', (event) => {
			if ('Escape' === event.key) {
				setOpen(false, true);
			}
		});
	});

	applyPreferences();
}
